// Tracks an SSO redirect completing on this page load. Read from the URL at
// import time, before bootstrap configures Amplify and strips the token. A
// store, not a flag: failures arrive as a Hub event, after the render that
// decided to wait.

/** How far an SSO redirect has got, if there was one. */
type OAuthCallbackStatus = "none" | "pending" | "failed";

/** Past Amplify's own 10s OAuth timeout, for failures that never reach the Hub. */
const OAUTH_CALLBACK_TIMEOUT_MS = 15_000;

// Did Cognito redirect us back here? On success it returns tokens in the hash
// (`#access_token=`) or a code in the query. When the IdP turns the user away —
// they aren't assigned the app, say — it hands Cognito an error instead, and
// Cognito bounces us back as `#error=access_denied` with no token at all. Same
// check Amplify makes, so whichever arrives, its failure reaches us on the Hub.
const hasOAuthResponse =
  /[#&](access_token|error)=/.test(window.location.hash) ||
  /[?&](code|error)=/.test(window.location.search);

let status: OAuthCallbackStatus = hasOAuthResponse ? "pending" : "none";
let timeoutId: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function setStatus(next: OAuthCallbackStatus): void {
  if (status === next) {
    return;
  }
  status = next;
  // Or a sign-in that succeeded would be marked failed once it elapsed.
  if (next !== "pending" && timeoutId !== undefined) {
    clearTimeout(timeoutId);
    timeoutId = undefined;
  }
  for (const listener of listeners) {
    listener();
  }
}

if (status === "pending") {
  timeoutId = setTimeout(failOAuthCallback, OAUTH_CALLBACK_TIMEOUT_MS);
}

/** Current status. */
export function getOAuthCallbackStatus(): OAuthCallbackStatus {
  return status;
}

/** Subscribes to status changes; returns the unsubscribe. */
export function subscribeOAuthCallback(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Nothing left to wait on. */
export function clearOAuthCallback(): void {
  setStatus("none");
}

/**
 * Amplify couldn't turn the redirect into a session. Called from bootstrap's
 * Hub handler — the auth machine ignores `signIn_failure`, so nothing else
 * would stop the loader.
 */
export function failOAuthCallback(): void {
  if (status === "pending") {
    setStatus("failed");
  }
}

export type { OAuthCallbackStatus };
